import { toPercentage } from '@dxp/core/helpers';
import { ChannelGroupType } from '@dxp/core/types';
import {
    IBusinessUnit,
    IBusinessUnitStatistics,
} from '../business-unit/business-unit';
import { ITelXLKpiQueueThreshold } from '../rbac/queue-kpi';
import { IMetric, IValueWithMetric } from '../metrics/metric';

export interface ITenant {
    id: string;
    name: string;
    kpiTelXLThreshold: ITelXLKpiQueueThreshold;
    businessUnits: IBusinessUnit[];
    activeBusinessUnits: IBusinessUnit[];
    channelGroups: ChannelGroupType[];

    statistics(index?: string): ITenantStatistics;
}

export interface ITenantStatistics {
    answeredWithinSLAPercentage: IValueWithMetric;
    answeredWithinSLAPercentageMetricByBusinessUnit: IMetric[];
    waiting: IValueWithMetric;
    connected: number;
    success: number;
    complete: number;
    timedOut: number;
    abandoned: number;
    outOfSla: number;
    currentLongestWaitingTimeInSeconds: IValueWithMetric;
    longestWaitingTimeInSeconds: IValueWithMetric;
    avgWaitingTimeInSeconds: IValueWithMetric;
    avgHandlingTimeInSeconds: IValueWithMetric;
}

export class Tenant implements ITenant {
    readonly id: string;
    readonly name: string;
    readonly #businessUnits: IBusinessUnit[];
    readonly #kpiTelXLThreshold: ITelXLKpiQueueThreshold;

    constructor(
        id: string,
        name: string,
        businessUnits: IBusinessUnit[],
        kpiTelXLThreshold: ITelXLKpiQueueThreshold,
    ) {
        this.id = id;
        this.name = name;
        this.#businessUnits = businessUnits;
        this.#kpiTelXLThreshold = kpiTelXLThreshold;
    }

    get businessUnits(): IBusinessUnit[] {
        return this.#businessUnits;
    }

    get activeBusinessUnits(): IBusinessUnit[] {
        return this.#businessUnits.filter(bu => bu.hasActivity);
    }

    get configuredBusinessUnits(): IBusinessUnit[] {
        return this.#businessUnits.filter(bu => bu.hasConfigured);
    }

    get kpiTelXLThreshold(): ITelXLKpiQueueThreshold {
        return this.#kpiTelXLThreshold;
    }

    get channelGroups(): ChannelGroupType[] {
        return [
            ...new Set(
                this.configuredBusinessUnits.flatMap(bu => bu.channelGroups),
            ),
        ];
    }

    statistics(index?: ChannelGroupType): ITenantStatistics {
        if (index === undefined) {
            return this.#calculatePerformance(
                this.activeBusinessUnits.map(bu => bu.statistics()),
            );
        }

        return this.#calculatePerformance(
            this.activeBusinessUnits.map(bu => bu.statistics(index)),
        );
    }

    #calculatePerformance(
        businessUnitStatistics: IBusinessUnitStatistics[],
    ): ITenantStatistics {
        if (businessUnitStatistics.length === 0) {
            return tenantStatisticDefault();
        }

        const answeredWithinSLAPercentageMetricByBusinessUnit =
            businessUnitStatistics.map(
                bus => bus.answeredWithinSLAPercentage.metric,
            );

        const answeredWithinSLAPercentage = Math.round(
            businessUnitStatistics.reduce(
                (total: number, bus: IBusinessUnitStatistics) => {
                    return total + bus.answeredWithinSLAPercentage.value;
                },
                0,
            ) / businessUnitStatistics.length,
        );

        const waiting = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.waiting.value;
            },
            0,
        );

        const connected = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.connected;
            },
            0,
        );

        const success = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.success;
            },
            0,
        );

        const complete = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.complete;
            },
            0,
        );

        const outOfSla = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.outOfSla;
            },
            0,
        );

        const timedOut = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.timedOut;
            },
            0,
        );

        const abandoned = businessUnitStatistics.reduce(
            (total: number, bus: IBusinessUnitStatistics) => {
                return total + bus.abandoned;
            },
            0,
        );

        const avgHandlingTimeQueues = businessUnitStatistics.filter(
            bus => bus.avgHandlingTimeInSeconds.value > 0,
        ).length;

        const averageHandlingTimeSecondsInSeconds =
            avgHandlingTimeQueues > 0
                ? businessUnitStatistics.reduce(
                      (total: number, bus: IBusinessUnitStatistics) => {
                          return total + bus.avgHandlingTimeInSeconds.value;
                      },
                      0,
                  ) / avgHandlingTimeQueues
                : 0;

        const longestWaitingTimeInSeconds = Math.max(
            ...businessUnitStatistics.map(
                bus => bus.longestWaitingTimeInSeconds.value,
            ),
        );

        const avgWaitingTimeInSeconds = Math.max(
            ...businessUnitStatistics.map(
                bus => bus.avgWaitingTimeInSeconds.value,
            ),
        );

        const currentLongestWaitingTimeStamp = Math.max(
            ...businessUnitStatistics.map(
                bus => bus.currentLongestWaitingTimeInSeconds.value,
            ),
        );

        return {
            answeredWithinSLAPercentageMetricByBusinessUnit:
                answeredWithinSLAPercentageMetricByBusinessUnit,
            answeredWithinSLAPercentage: {
                value: answeredWithinSLAPercentage,
                metric: {
                    percent: answeredWithinSLAPercentage,
                    states: this.kpiTelXLThreshold.slaThreshold.enabled
                        ? [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold.slaThreshold.low,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold.slaThreshold.high,
                                  ),
                                  state: 'success',
                              },
                          ]
                        : [],
                },
            },
            waiting: {
                value: waiting,
                metric: this.kpiTelXLThreshold.maxConversationsWaiting.enabled
                    ? {
                          percent: toPercentage(
                              waiting /
                                  this.kpiTelXLThreshold.maxConversationsWaiting
                                      .high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .maxConversationsWaiting.low /
                                          this.kpiTelXLThreshold
                                              .maxConversationsWaiting.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            connected: connected,
            currentLongestWaitingTimeInSeconds: {
                value: currentLongestWaitingTimeStamp,
                metric: this.#kpiTelXLThreshold.maxLongestWaitTimeSeconds
                    .enabled
                    ? {
                          percent: this.#kpiTelXLThreshold
                              .maxLongestWaitTimeSeconds.high
                              ? toPercentage(
                                    currentLongestWaitingTimeStamp /
                                        this.#kpiTelXLThreshold
                                            .maxLongestWaitTimeSeconds.high,
                                )
                              : 0,
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.#kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            longestWaitingTimeInSeconds: {
                value: longestWaitingTimeInSeconds,
                metric: this.kpiTelXLThreshold.maxLongestWaitTimeSeconds.enabled
                    ? {
                          percent: toPercentage(
                              longestWaitingTimeInSeconds /
                                  this.kpiTelXLThreshold
                                      .maxLongestWaitTimeSeconds.high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            avgWaitingTimeInSeconds: {
                value: avgWaitingTimeInSeconds,
                metric: this.kpiTelXLThreshold.averageWaitTimeSeconds.enabled
                    ? {
                          percent: toPercentage(
                              (avgWaitingTimeInSeconds ?? 0) /
                                  this.kpiTelXLThreshold.averageWaitTimeSeconds
                                      .high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.kpiTelXLThreshold
                                          .averageWaitTimeSeconds.low /
                                          this.kpiTelXLThreshold
                                              .averageWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            success: success,
            complete: complete,
            outOfSla: outOfSla,
            timedOut: timedOut,
            abandoned: abandoned,
            avgHandlingTimeInSeconds: {
                value: averageHandlingTimeSecondsInSeconds,
                metric:
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds &&
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds.enabled
                        ? {
                              percent: toPercentage(
                                  averageHandlingTimeSecondsInSeconds /
                                      this.#kpiTelXLThreshold
                                          .averageHandlingTimeSeconds.target,
                              ),
                              states: [
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.low /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'warning',
                                  },
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.high /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'danger',
                                  },
                              ],
                          }
                        : { percent: 0, states: [] },
            },
        };
    }
}

export const tenantStatisticDefault = (): ITenantStatistics => {
    return {
        answeredWithinSLAPercentageMetricByBusinessUnit: [],
        answeredWithinSLAPercentage: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        waiting: { value: 0, metric: { percent: 0, states: [] } },
        connected: 0,
        success: 0,
        complete: 0,
        timedOut: 0,
        abandoned: 0,
        outOfSla: 0,
        longestWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        avgWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        currentLongestWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        avgHandlingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
    };
};
